import React, { useState } from 'react';
import { List, Calculator, Info, Download, FileText, FileSpreadsheet, Package, ChevronDown, Filter } from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';
import { CollapsibleSection } from './ReportComponents';
import api from '../../services/api';

const ScheduleCReport = ({
  data,
  selectedYear,
  expandedSections,
  toggleSection,
  excludePreBusiness = true,
  onExcludePreBusinessChange
}) => {
  const [exporting, setExporting] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const handleExport = async (type) => {
    setExporting(type);
    setShowExportMenu(false);

    try {
      let endpoint = '';
      let filename = '';
      let contentType = 'application/octet-stream';

      switch (type) {
        case 'pdf':
          endpoint = `/reports/schedule-c/pdf?year=${selectedYear}&excludePreBusiness=${excludePreBusiness}`;
          filename = `ScheduleC_${selectedYear}.pdf`;
          contentType = 'application/pdf';
          break;
        case 'expenses-csv':
          endpoint = `/reports/expenses/csv?year=${selectedYear}&excludePreBusiness=${excludePreBusiness}`;
          filename = `Expenses_${selectedYear}.csv`;
          contentType = 'text/csv';
          break;
        case 'revenue-csv':
          endpoint = `/reports/revenue/csv?year=${selectedYear}`;
          filename = `Revenue_${selectedYear}.csv`;
          contentType = 'text/csv';
          break;
        case 'schedule-csv':
          endpoint = `/reports/schedule-c/csv?year=${selectedYear}&excludePreBusiness=${excludePreBusiness}`;
          filename = `ScheduleC_${selectedYear}.csv`;
          contentType = 'text/csv';
          break;
        case 'tax-package':
          endpoint = `/reports/tax-package?year=${selectedYear}&excludePreBusiness=${excludePreBusiness}`;
          filename = `TaxPackage_${selectedYear}.zip`;
          contentType = 'application/zip';
          break;
        default:
          return;
      }

      const response = await api.get(endpoint, { responseType: 'blob' });

      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export. Please try again.');
    } finally {
      setExporting(null);
    }
  };

  const handlePreBusinessFilterChange = (checked) => {
    if (onExcludePreBusinessChange) {
      onExcludePreBusinessChange(checked);
    }
  };

  if (!data) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No data available for this year.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Info */}
      <div className="card p-3 bg-gray-50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-bold text-gray-900">{data.businessName}</h2>
            <p className="text-xs text-gray-500">{data.entityType} • Tax Year {selectedYear}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">For tax prep only</p>
          </div>
        </div>
      </div>

      {/* Filter Options - Collapsible */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden print:hidden">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
              <Filter size={16} className="text-gray-600" />
            </div>
            <div className="text-left">
              <h3 className="text-sm font-semibold text-gray-900">Filter Options</h3>
              <p className="text-xs text-gray-600">
                {excludePreBusiness ? 'Startup costs excluded (Recommended)' : 'Includes startup costs'}
              </p>
            </div>
          </div>
          <ChevronDown size={16} className={`text-gray-400 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>

        {showFilters && (
          <div className="px-5 pb-5 border-t border-gray-100">
            <div className="mt-4 flex items-start justify-between">
              <div className="flex-1 pr-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-900">Startup Costs</span>
                </div>
                <p className="text-xs text-gray-600">
                  Business expenses from before you officially started
                </p>
                <div className="mt-2">
                  <div className={`text-xs font-medium ${excludePreBusiness ? 'text-green-700' : 'text-amber-700'}`}>
                    {excludePreBusiness ? '✓ Excluded from Schedule C (Recommended)' : '⚠️ Included in Schedule C'}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {excludePreBusiness
                      ? 'Startup costs are usually handled separately from regular yearly expenses'
                      : 'May cause double-counting if you claim startup deductions'
                    }
                  </p>
                </div>
              </div>
              <button
                onClick={() => handlePreBusinessFilterChange(!excludePreBusiness)}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  excludePreBusiness ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 ${
                    excludePreBusiness ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tax Package Download */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Package size={20} className="text-green-600" />
              <h3 className="text-lg font-semibold text-green-900">Ready for Tax Time</h3>
            </div>
            <p className="text-sm text-green-700 mb-2">
              Everything you or your tax preparer needs in one download
            </p>
            <div className="text-xs text-green-600">
              <strong>What's included:</strong> Schedule C form, detailed expenses, revenue records, and summary reports
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {excludePreBusiness && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                  ✓ Startup costs excluded
                </span>
              )}
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                ✓ Personal work excluded
              </span>
            </div>
          </div>
          <button
            onClick={() => handleExport('tax-package')}
            disabled={exporting === 'tax-package'}
            className="btn btn-primary bg-green-600 hover:bg-green-700 flex items-center gap-2 whitespace-nowrap px-6 py-3 text-base font-semibold"
          >
            {exporting === 'tax-package' ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Preparing...
              </>
            ) : (
              <>
                <Download size={18} />
                Download Tax Package
              </>
            )}
          </button>
        </div>
      </div>

      {/* Individual Export Options */}
      <div className="relative print:hidden">
        <button
          onClick={() => setShowExportMenu(!showExportMenu)}
          className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
        >
          <Download size={14} />
          Export individual files
          <ChevronDown size={14} className={`transition-transform ${showExportMenu ? 'rotate-180' : ''}`} />
        </button>

        {showExportMenu && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[200px]">
            <button
              onClick={() => handleExport('pdf')}
              disabled={exporting === 'pdf'}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
            >
              <FileText size={14} className="text-red-500" />
              Schedule C PDF
              {exporting === 'pdf' && <span className="text-xs text-gray-400">...</span>}
            </button>
            <button
              onClick={() => handleExport('expenses-csv')}
              disabled={exporting === 'expenses-csv'}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
            >
              <FileSpreadsheet size={14} className="text-green-500" />
              Expenses CSV
              {exporting === 'expenses-csv' && <span className="text-xs text-gray-400">...</span>}
            </button>
            <button
              onClick={() => handleExport('revenue-csv')}
              disabled={exporting === 'revenue-csv'}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
            >
              <FileSpreadsheet size={14} className="text-blue-500" />
              Revenue CSV
              {exporting === 'revenue-csv' && <span className="text-xs text-gray-400">...</span>}
            </button>
            <button
              onClick={() => handleExport('schedule-csv')}
              disabled={exporting === 'schedule-csv'}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
            >
              <FileSpreadsheet size={14} className="text-purple-500" />
              Schedule C CSV
              {exporting === 'schedule-csv' && <span className="text-xs text-gray-400">...</span>}
            </button>
          </div>
        )}
      </div>

      {/* Gross Receipts */}
      <div className="card p-0 overflow-hidden">
        <div className="flex items-center justify-between p-3 bg-green-50">
          <div>
            <p className="text-xs font-medium text-green-700">Line 1: Gross Receipts</p>
            <p className="text-xs text-green-600">Business invoices only</p>
          </div>
          <p className="text-xl font-bold text-green-900">{formatCurrency(data.grossReceipts)}</p>
        </div>
      </div>

      {/* Personal/Free work - reference only */}
      {data.personalServicesValue > 0 && (
        <div className="card p-3 bg-purple-50 border-purple-200">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Info size={16} className="text-purple-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-purple-900">Personal/Free Work Not Included</p>
              <p className="text-xs text-purple-700 mt-0.5">
                {formatCurrency(data.personalServicesValue)} of work across {data.personalServicesCount} job{data.personalServicesCount !== 1 ? 's' : ''} was
                done at no charge. No money was received, so it is not part of Line 1. Tracked for your records only.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Expense Lines */}
      <CollapsibleSection
        title="Expense Lines"
        icon={List}
        isExpanded={expandedSections.expenseLines}
        onToggle={() => toggleSection('expenseLines')}
        count={data.expenseLines.length}
      >
        {data.expenseLines.length === 0 ? (
          <p className="text-gray-500 text-center py-6 text-sm">No deductible expenses</p>
        ) : (
          <>
            <div className="divide-y divide-gray-50">
              {data.expenseLines.map((line, index) => (
                <div key={index} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      <span className="text-gray-400">L{line.lineNumber}:</span> {line.description}
                    </p>
                    <p className="text-xs text-gray-400">{line.expenseCount} expense{line.expenseCount !== 1 ? 's' : ''}</p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 ml-3">{formatCurrency(line.amount)}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-200">
              <div className="flex items-center justify-between p-3 bg-red-50">
                <p className="text-sm font-medium text-red-700">Total Expenses</p>
                <p className="text-lg font-bold text-red-900">{formatCurrency(data.totalExpenses)}</p>
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50">
                <p className="text-sm font-medium text-blue-700">Line 31: Net Profit</p>
                <p className="text-lg font-bold text-blue-900">{formatCurrency(data.tentativeProfit)}</p>
              </div>
            </div>
          </>
        )}
      </CollapsibleSection>

      {/* Tax Estimates */}
      <CollapsibleSection
        title="Tax Estimates"
        icon={Calculator}
        isExpanded={expandedSections.taxEstimates}
        onToggle={() => toggleSection('taxEstimates')}
      >
        <div className="p-3 bg-amber-50 border-b border-amber-100">
          <p className="text-xs text-amber-700 flex items-start gap-1.5">
            <Info size={12} className="flex-shrink-0 mt-0.5" />
            Rough estimates only. Consult a tax professional.
          </p>
        </div>
        <div className="divide-y divide-gray-50">
          <div className="flex items-center justify-between px-3 py-2">
            <div>
              <p className="text-sm font-medium text-gray-900">Self-Employment Tax</p>
              <p className="text-xs text-gray-400">SS + Medicare (15.3%)</p>
            </div>
            <p className="text-sm font-semibold text-gray-900">{formatCurrency(data.estimatedSelfEmploymentTax)}</p>
          </div>
          {data.stateIncomeTaxRate > 0 && (
            <div className="flex items-center justify-between px-3 py-2">
              <div>
                <p className="text-sm font-medium text-gray-900">State Income Tax</p>
                <p className="text-xs text-gray-400">Est. {data.stateIncomeTaxRate}%</p>
              </div>
              <p className="text-sm font-semibold text-gray-900">{formatCurrency(data.estimatedStateIncomeTax)}</p>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Tax Tip */}
      <div className="card p-3 bg-blue-50 border-blue-200">
        <p className="text-xs text-blue-800 flex items-start gap-1.5">
          <Info size={12} className="flex-shrink-0 mt-0.5" />
          <span>
            <strong>Tip:</strong> Keep the Tax Package ZIP with your tax records - it has everything you or your preparer need
            to prepare your Schedule C. Keep your receipts organized in case of an audit.
            {excludePreBusiness && " Pre-business expenses are excluded as recommended for proper tax treatment."}
            {" Personal/Free services are excluded from taxable income."}
          </span>
        </p>
      </div>
    </div>
  );
};

export default ScheduleCReport;